import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import { ComponentErrorBoundary } from "core/components/ComponentErrorBoundary";
import { ErrorComponent } from "core/components/ErrorComponent";
import { GridAreaContainer } from "core/components/GridAreaContainer";
import { ImageOverlay } from "core/components/ImageOverlay";
import { LinkButton } from "core/components/LinkButton";
import { useIsFeatureFlagEnabled } from "core/components/useFeatureFlags";
import type { TFunction } from "core/entities";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useLocale } from "core/hooks/useLocale";
import { useNavigate } from "core/hooks/useNavigate";
import Image from "next/image";
import { useTranslation } from "react-i18next";

function Wrapper({ t, children }: { t: TFunction; children: React.ReactNode }) {
  return (
    <ComponentErrorBoundary fallBackBoxProps={{ sx: { gridArea: "map" } }}>
      <GridAreaContainer
        gridArea="map"
        title={t("gridAreaContainerTitle", "Shop Map")}
      >
        {children}
      </GridAreaContainer>
    </ComponentErrorBoundary>
  );
}

export function ShopMapWidget() {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ShopMapWidget",
  });
  return (
    <Wrapper t={t}>
      <StaticShopMapWidget t={t} />
    </Wrapper>
  );
}

export function StaticShopMapWidget({ t }: { t: TFunction }) {
  const navigate = useNavigate();
  const locale = useLocale();
  const brcy = useBrcyCode();
  const shopMapPageFeatureFlagEnabled = useIsFeatureFlagEnabled(
    "interactiveMapPageActive"
  );
  const { data, isLoading, isError } = useCentrixApi({
    path: "/api/app/pitstop-shop-map/brand-country-shop-map",
    parameters: {
      query: {
        brcy,
        locale,
      },
    },
  });
  if (isError) {
    return (
      <Paper sx={{ p: 3, height: "100%", aspectRatio: "1/1" }}>
        <ErrorComponent apiError={isError} />
      </Paper>
    );
  }
  if (isLoading || !data) {
    return (
      <Box sx={{ height: "100%", aspectRatio: "1/1" }}>
        <Skeleton variant="rounded" height="100%" />
      </Box>
    );
  }
  if (!shopMapPageFeatureFlagEnabled) {
    return (
      <Paper overflow="hidden" component={Box}>
        <Image
          src={data.imageUrl!}
          alt={t("staticMapAltText", "Shop Map")}
          blurDataURL={data.blurDataUrl ?? ""}
          placeholder={data.blurDataUrl ? "empty" : "blur"}
          height={data.height ?? 0}
          width={data.width ?? 0}
          style={{
            objectFit: "cover",
            height: "auto",
            width: "100%",
            display: "block",
          }}
          unoptimized
        />
      </Paper>
    );
  }
  return (
    <ImageOverlay
      altText={t("staticMapAltText", "Shop Map")}
      imageSrc={data.imageUrl!}
      blurDataUrl={data.blurDataUrl}
      height={data.height ?? 0}
      width={data.width ?? 0}
      onClick={() => navigate("/interactive-map")}
      overlaySlot={
        <LinkButton
          variant="contained"
          id="interactive-map-button"
          href="/interactive-map"
        >
          {t("viewInteractiveMapCta", "Interactive Map")}
        </LinkButton>
      }
    />
  );
}
